import React from 'react';
import Chart from 'react-apexcharts';
import { FormControl, InputLabel, Select as MuiSelect, MenuItem } from "@mui/material";

const ConsultantOnlineTimeChart = ({ data = [], topN = 5, onTopNChange }) => {
  console.log(data, "ConsultantOnlineTimeChart");
  
  // Filter out invalid data and ensure we have valid consultant data
  const validData = Array.isArray(data) ? data.filter(item => {
    if (!item) return false;
    // Check if consultant has a name
    const hasName = item.consultantName || item.name;
    if (!hasName) return false;
    
    const onlineTime = parseFloat(item.onlineTime) || 0;
    const busyTime = parseFloat(item.busyTime) || 0;
    // Include all consultants (even with 0 time) to show the chart structure
    return true;
  }) : [];
  
  console.log('ConsultantOnlineTimeChart - Raw data:', data);
  console.log('ConsultantOnlineTimeChart - Valid data:', validData);
  
  // Sort by busyTime (descending) for ranking - ranking based on busy time
  const sortedData = [...validData].sort((a, b) => {
    const aBusyTime = parseFloat(a.busyTime) || 0;
    const bBusyTime = parseFloat(b.busyTime) || 0;
    return bBusyTime - aBusyTime;
  });
  
  // Limit to topN consultants
  const topNData = sortedData.slice(0, topN);
  
  console.log('ConsultantOnlineTimeChart - sortedData:', sortedData);
  console.log('ConsultantOnlineTimeChart - topNData (limited to', topN, '):', topNData);

  // Prepare data for stacked bar chart
  // Chart will show: Online Time and Busy Time (Chat + Call combined) as two segments
  const chartSeries = topNData.map((consultant, index) => {
    // Handle both string and number formats - ensure we get valid numbers
    let onlineTime = 0;
    let busyTime = 0;
    
    if (consultant.onlineTime !== undefined && consultant.onlineTime !== null) {
      onlineTime = typeof consultant.onlineTime === 'string' 
        ? parseFloat(consultant.onlineTime) 
        : Number(consultant.onlineTime);
      if (isNaN(onlineTime)) onlineTime = 0;
    }
    
    if (consultant.busyTime !== undefined && consultant.busyTime !== null) {
      busyTime = typeof consultant.busyTime === 'string' 
        ? parseFloat(consultant.busyTime) 
        : Number(consultant.busyTime);
      if (isNaN(busyTime)) busyTime = 0;
    }
    
    console.log(`Consultant ${index + 1} (${consultant.consultantName || consultant.name}):`, {
      rawOnlineTime: consultant.onlineTime,
      rawBusyTime: consultant.busyTime,
      parsedOnlineTime: onlineTime,
      parsedBusyTime: busyTime
    });
    
    return {
      onlineTime: onlineTime,
      busyTime: busyTime
    };
  });

  console.log('ConsultantOnlineTimeChart - chartSeries:', chartSeries);
  console.log('ConsultantOnlineTimeChart - Series Data:', {
    onlineTime: chartSeries.map(item => item.onlineTime),
    chatTime: chartSeries.map(item => item.chatTime),
    callTime: chartSeries.map(item => item.callTime)
  });

  // Ensure we have data before creating chart
  if (topNData.length === 0) {
    return (
      <div className="chart-container">
        <div className="chart-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h4>Top {topN} Consultants Online Time</h4>
            <p>Ranked by total online time</p>
          </div>
        </div>
        <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>
          No consultant online time data available
        </div>
      </div>
    );
  }

  // Prepare series data - ensure all values are numbers
  const onlineTimeData = chartSeries.map(item => {
    const val = Number(item.onlineTime) || 0;
    return val;
  });
  const busyTimeData = chartSeries.map(item => {
    const val = Number(item.busyTime) || 0;
    return val;
  });

  // Calculate conversion percentage: (Busy Time * 100) / Online Time
  const conversionPercentages = topNData.map((consultant, index) => {
    const onlineTime = onlineTimeData[index] || 0;
    const busyTime = busyTimeData[index] || 0;
    if (onlineTime > 0) {
      return ((busyTime * 100) / onlineTime).toFixed(2);
    }
    return '0.00';
  });

  // Check if we have any non-zero data
  const hasData = onlineTimeData.some(v => v > 0) || busyTimeData.some(v => v > 0);
  
  console.log('ConsultantOnlineTimeChart - Final Series Arrays:', {
    onlineTime: onlineTimeData,
    busyTime: busyTimeData,
    categories: topNData.map(c => c.consultantName || c.name),
    hasData: hasData,
    totalOnlineTime: onlineTimeData.reduce((a, b) => a + b, 0),
    totalBusyTime: busyTimeData.reduce((a, b) => a + b, 0)
  });
  
  if (!hasData && topNData.length > 0) {
    console.warn('ConsultantOnlineTimeChart - Warning: All time values are 0!');
  }

  const chartData = {
    series: [
      {
        name: 'Online Time',
        data: onlineTimeData
      },
      {
        name: 'Busy Time (Chat + Call)',
        data: busyTimeData
      }
    ],
    options: {
      chart: {
        type: 'bar',
        height: 350,
        stacked: true,
        toolbar: {
          show: false
        }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          dataLabels: {
            total: {
              enabled: false
            }
          }
        }
      },
      stroke: {
        width: 1,
        colors: ['#fff']
      },
      dataLabels: {
        enabled: true,
        formatter: function (val, opts) {
          // Show labels for all segments - check if value is valid
          const value = parseFloat(val) || 0;
          return value > 0 ? value.toFixed(2) + 'h' : '';
        },
        offsetX: 0,
        style: {
          fontSize: '12px',
          colors: ['#fff'],
          fontWeight: 'bold'
        }
      },
      colors: ['#10b981', '#3b82f6'], // Green: Online Time, Blue: Busy Time (Chat + Call)
      xaxis: {
        categories: topNData.length > 0 ? topNData.map((consultant, index) => {
          const name = consultant.consultantName || consultant.name || 'Unknown Consultant';
          const conversionPercent = conversionPercentages[index] || '0.00';
          return `${name} - ${conversionPercent}%`;
        }) : [],
        labels: {
          formatter: function (val) {
            return val;
          }
        },
        title: {
          text: undefined
        }
      },
      yaxis: {
        title: {
          text: undefined
        }
      },
      grid: {
        borderColor: '#f1f1f1',
        strokeDashArray: 2,
        xaxis: {
          lines: {
            show: true
          }
        },
        yaxis: {
          lines: {
            show: false
          }
        }
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val.toFixed(2) + ' hours';
          }
        }
      },
      fill: {
        opacity: 1
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left',
        offsetX: 40
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              height: 300
            },
            dataLabels: {
              style: {
                fontSize: '10px'
              }
            }
          }
        }
      ]
    }
  };

  // Fixed height for scrollable chart (400px max, scrollable if more consultants)
  const chartHeight = 400;

  return (
    <div className="chart-container">
      <div className="chart-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h4>Top {topN} Consultants Online Time</h4>
          <p>Ranked by busy time (Chat + Call)</p>
        </div>
        <div style={{ minWidth: '180px' }}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>Filter</InputLabel>
            <MuiSelect
              value={topN || 5}
              onChange={(e) => onTopNChange && onTopNChange(e.target.value)}
              label="Filter"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#3b82f6',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#3b82f6',
                },
              }}
            >
              <MenuItem value={5}>Top 5</MenuItem>
              <MenuItem value={10}>Top 10</MenuItem>
              <MenuItem value={15}>Top 15</MenuItem>
              <MenuItem value={20}>Top 20</MenuItem>
              <MenuItem value={30}>Top 30</MenuItem>
            </MuiSelect>
          </FormControl>
        </div>
      </div>
      <div style={{ 
        maxHeight: `${chartHeight}px`, 
        overflowY: 'auto', 
        overflowX: 'hidden',
        scrollbarWidth: 'thin',
        scrollbarColor: '#cbd5e1 #f1f5f9'
      }}>
        <style>{`
          div::-webkit-scrollbar {
            width: 8px;
          }
          div::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          div::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          div::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}</style>
        <Chart
          key={`chart-${topNData.length}-${JSON.stringify(onlineTimeData.slice(0, 3))}`}
          options={chartData.options}
          series={chartData.series}
          type="bar"
          height={Math.max(chartHeight, topNData.length * 40)}
        />
      </div>
    </div>
  );
};

export default ConsultantOnlineTimeChart;

